/**
 * SSE (Server-Sent Events) service for AskAsper chat
 */

import { SSERequestParams } from '../types';
import { ASK_ASPER_BASE_URL } from '../config';

/**
 * SSE listener function
 * Handles streaming responses from the AskAsper API.
 * onMessage receives the parsed JSON payload (shape depends on event type).
 */
export const listenToSSE = async (
    params: SSERequestParams,
    onMessage?: (json: unknown) => void,
    onDone?: () => void,
    onError?: (err: Error) => void,
): Promise<void> => {
    const url = `${ASK_ASPER_BASE_URL}llmcompiler-invoke`;
    const token = localStorage.getItem('accessToken');
    const TOKEN = `Bearer ${token}`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                Authorization: TOKEN,
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            throw new Error(`SSE connection error: ${response.status} ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('No reader available');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();

            if (done) {
                onDone?.();
                break;
            }

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n\n');
            buffer = lines.pop() || ''; // keep unfinished line

            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;

                try {
                    const json = JSON.parse(line.slice(6)); // after 'data: '

                    if (json.type === 'stream_complete') {
                        // Pass stream_complete to onMessage so it's processed in the same flow as prior messages (e.g. 'ai').
                        // Defer onDone so React can flush setState updates from heavy 'ai' processing before finalization.
                        // This prevents the race where handleDone runs with a stale loadingMessageRef before the ai update.
                        onMessage?.(json);
                        if (onDone) {
                            setTimeout(onDone, 0);
                        }
                        return;
                    }

                    if (json.type === 'error') {
                        onMessage?.(json);
                        return; // Stream ends; component finalizes in handleMessage
                    }

                    onMessage?.(json);
                } catch (e) {
                    console.error('Bad SSE JSON:', e, line);
                }
            }
        }
    } catch (err) {
        console.error('SSE failed:', err);
        onError?.(err instanceof Error ? err : new Error(String(err)));
    }
};
